import { useCallback, useEffect, useRef, useState, FormEvent } from "react";
import {
  Plus, Pencil, Trash2, Search, ChevronLeft, ChevronRight,
  Upload, Download, FileSpreadsheet, AlertTriangle, CheckCircle2, Package,
} from "lucide-react";
import { api, mensajeError } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/ui/PageHeader";
import { Modal } from "../components/ui/Modal";
import { Spinner } from "../components/ui/Spinner";
import { Badge } from "../components/ui/Badge";
import { EmptyState } from "../components/ui/EmptyState";

const categorias = [
  { value: "SERVICIO", label: "Servicio" },
  { value: "MEDICAMENTO", label: "Medicamento" },
  { value: "ALIMENTO", label: "Alimento" },
  { value: "ACCESORIO", label: "Accesorio" },
  { value: "OTRO", label: "Otro" },
];

const formularioVacio = {
  nombre: "",
  categoria: "SERVICIO",
  descripcion: "",
  sku: "",
  precioCompra: "0",
  precioVenta: "",
  unidad: "pieza",
  stockActual: "0",
  stockMinimo: "0",
  caducidad: "",
  proveedor: "",
  // Datos para poder facturar (CFDI 4.0) las ventas de este producto/
  // servicio — se copian a cada venta que lo incluya. Traen un default
  // seguro y genérico, no es obligatorio afinarlos para poder guardar.
  claveProdServ: "01010101",
  claveUnidadSat: "H87",
  objetoImpuesto: "02",
  tasaIva: "0.16",
};

// Un producto "maneja inventario" cuando sí le aplican stock/caducidad/
// proveedor. Por default esto se infiere de la categoría (los servicios no
// manejan inventario), pero el usuario lo puede activar manualmente aunque
// sea un servicio, o desactivarlo aunque sea un producto físico.
function infiereManejaInventario(p: any) {
  return p.categoria !== "SERVICIO" || (p.stockMinimo ?? 0) > 0 || !!p.caducidad || !!p.proveedor;
}

// Igual que con inventario: por default los datos de facturación (CFDI) no
// se muestran, se usan los valores genéricos de siempre. Si el producto ya
// traía alguno distinto al default (porque se afinó antes), se muestra el
// apartado abierto al editar.
function infiereManejaFacturacion(p: any) {
  return (
    (p.claveProdServ && p.claveProdServ !== "01010101") ||
    (p.claveUnidadSat && p.claveUnidadSat !== "H87") ||
    (p.objetoImpuesto && p.objetoImpuesto !== "02") ||
    (p.tasaIva !== undefined && p.tasaIva !== null && Number(p.tasaIva) !== 0.16)
  );
}

interface ErrorFilaCsv {
  fila: number;
  columna: string;
  mensaje: string;
}

interface FilaCsv {
  fila: number;
  accion: "crear" | "actualizar" | "error";
  sku: string | null;
  nombre: string;
  datos?: {
    categoria: string;
    precioVenta: number;
    stockActual: number;
  };
  errores: ErrorFilaCsv[];
}

interface VistaPreviaCsv {
  errorGeneral: string | null;
  filas: FilaCsv[];
  resumen: { crear: number; actualizar: number; errores: number };
}

function descargarBlob(blob: Blob, nombreArchivo: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombreArchivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function Inventario() {
  const { soloLectura } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [buscar, setBuscar] = useState("");

  const [modalAbierto, setModalAbierto] = useState(false);
  const [editando, setEditando] = useState<any | null>(null);
  const [form, setForm] = useState<any>(formularioVacio);
  const [manejaInventario, setManejaInventario] = useState(false);
  const [manejaFacturacion, setManejaFacturacion] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errorForm, setErrorForm] = useState<string | null>(null);

  // Carga/descarga de inventario por CSV.
  const inputCsvRef = useRef<HTMLInputElement>(null);
  const [modalCsvAbierto, setModalCsvAbierto] = useState(false);
  const [archivoCsvTexto, setArchivoCsvTexto] = useState<string | null>(null);
  const [nombreArchivoCsv, setNombreArchivoCsv] = useState("");
  const [cargandoVistaPrevia, setCargandoVistaPrevia] = useState(false);
  const [vistaPrevia, setVistaPrevia] = useState<VistaPreviaCsv | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [errorCsv, setErrorCsv] = useState<string | null>(null);
  const [resultadoCsv, setResultadoCsv] = useState<{ creados: number; actualizados: number } | null>(null);
  const [exportando, setExportando] = useState(false);

  // Catálogos SAT para los campos de facturación del producto (claves de
  // unidad/producto sugeridas y objeto de impuesto). Si no cargan, los
  // campos siguen siendo de texto libre y no bloquean nada.
  const [catalogosFactura, setCatalogosFactura] = useState<{
    clavesUnidadSugeridas: { value: string; label: string }[];
    clavesProdServSugeridas: { value: string; label: string }[];
    objetosImpuesto: { value: string; label: string }[];
  } | null>(null);

  useEffect(() => {
    api
      .get("/facturacion/catalogos")
      .then((res) =>
        setCatalogosFactura({
          clavesUnidadSugeridas: res.data?.clavesUnidadSugeridas ?? [],
          clavesProdServSugeridas: res.data?.clavesProdServSugeridas ?? [],
          objetosImpuesto: res.data?.objetosImpuesto ?? [],
        })
      )
      .catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const res = await api.get("/productos", { params: { pagina, porPagina: 20, buscar: buscar || undefined } });
      setItems(res.data.items ?? []);
      setTotalPaginas(res.data.totalPaginas ?? 1);
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setCargando(false);
    }
  }, [pagina, buscar]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  function abrirCrear() {
    if (soloLectura) return;
    setForm(formularioVacio);
    setManejaInventario(false);
    setManejaFacturacion(false);
    setEditando(null);
    setErrorForm(null);
    setModalAbierto(true);
  }

  function abrirEditar(p: any) {
    setForm({
      nombre: p.nombre ?? "",
      categoria: p.categoria ?? "OTRO",
      descripcion: p.descripcion ?? "",
      sku: p.sku ?? "",
      precioCompra: String(p.precioCompra ?? 0),
      precioVenta: String(p.precioVenta ?? 0),
      unidad: p.unidad ?? "pieza",
      stockActual: String(p.stockActual ?? 0),
      stockMinimo: String(p.stockMinimo ?? 0),
      caducidad: p.caducidad ? String(p.caducidad).slice(0, 10) : "",
      proveedor: p.proveedor ?? "",
      claveProdServ: p.claveProdServ ?? "01010101",
      claveUnidadSat: p.claveUnidadSat ?? "H87",
      objetoImpuesto: p.objetoImpuesto ?? "02",
      tasaIva: String(p.tasaIva ?? 0.16),
    });
    setManejaInventario(infiereManejaInventario(p));
    setManejaFacturacion(infiereManejaFacturacion(p));
    setEditando(p);
    setErrorForm(null);
    setModalAbierto(true);
  }

  function cambiarCategoria(nuevaCategoria: string) {
    setForm((f: any) => ({ ...f, categoria: nuevaCategoria }));
    // Por default, los servicios no manejan inventario y el resto sí — pero
    // esto es solo la sugerencia inicial; el usuario la puede cambiar debajo.
    setManejaInventario(nuevaCategoria !== "SERVICIO");
  }

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    setErrorForm(null);
    try {
      const payload: Record<string, any> = {
        nombre: form.nombre,
        categoria: form.categoria,
        descripcion: form.descripcion || undefined,
        sku: form.sku || undefined,
        precioCompra: Number(form.precioCompra) || 0,
        precioVenta: Number(form.precioVenta) || 0,
        unidad: form.unidad || "pieza",
        // Si "no aplica" (no maneja inventario), se guardan los valores por
        // default limpios en vez de lo que haya quedado escrito y oculto.
        stockActual: manejaInventario ? Number(form.stockActual) || 0 : 0,
        stockMinimo: manejaInventario ? Number(form.stockMinimo) || 0 : 0,
        // null (no undefined) para que, al editar, realmente se borre el
        // valor viejo en vez de quedarse tal cual estaba en la base de datos.
        caducidad: manejaInventario && form.caducidad ? form.caducidad : null,
        proveedor: manejaInventario ? form.proveedor || null : null,
        claveProdServ: manejaFacturacion ? (form.claveProdServ || "01010101") : "01010101",
        claveUnidadSat: manejaFacturacion ? (form.claveUnidadSat || "H87") : "H87",
        objetoImpuesto: manejaFacturacion ? (form.objetoImpuesto || "02") : "02",
        tasaIva: manejaFacturacion ? (Number(form.tasaIva) || 0) : 0.16,
      };
      if (editando) {
        await api.put(`/productos/${editando.id}`, payload);
      } else {
        await api.post("/productos", payload);
      }
      setModalAbierto(false);
      cargar();
    } catch (err) {
      setErrorForm(mensajeError(err));
    } finally {
      setGuardando(false);
    }
  }

  async function eliminar(p: any) {
    if (!window.confirm(`¿Eliminar "${p.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/productos/${p.id}`);
      cargar();
    } catch (err) {
      alert(mensajeError(err));
    }
  }

  async function descargarPlantilla() {
    const res = await api.get("/inventario/plantilla-csv", { responseType: "blob" });
    descargarBlob(res.data, "plantilla-productos-y-servicios.csv");
  }

  async function exportarInventario() {
    setExportando(true);
    try {
      const res = await api.get("/inventario/exportar-csv", { responseType: "blob" });
      descargarBlob(res.data, "productos-y-servicios.csv");
    } catch (err) {
      alert(mensajeError(err));
    } finally {
      setExportando(false);
    }
  }

  function abrirModalCsv() {
    setModalCsvAbierto(true);
    setArchivoCsvTexto(null);
    setNombreArchivoCsv("");
    setVistaPrevia(null);
    setErrorCsv(null);
    setResultadoCsv(null);
  }

  // Excel en Windows (sobre todo en español/Latinoamérica) guarda "CSV
  // (delimitado por comas)" en Windows-1252 (ANSI), no en UTF-8. Si el
  // archivo se lee siempre forzando UTF-8 (como hacía antes con
  // readAsText(archivo, "utf-8")), los acentos y eñes quedan mal ("Ba?o" o
  // el carácter de reemplazo "�") — por eso "Baño" no se veía bien al
  // importar aunque escribirlo directo en el formulario sí funcionaba.
  // Se leen los bytes crudos y se prueba primero UTF-8 en modo estricto; si
  // el archivo NO es UTF-8 válido, se vuelve a leer como Windows-1252 — así
  // funciona igual con un CSV exportado de Excel o de Google Sheets. Mismo
  // criterio que ya se usa para el CSV de campañas.
  function decodificarCsv(buffer: ArrayBuffer): string {
    try {
      return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
    } catch {
      return new TextDecoder("windows-1252").decode(buffer);
    }
  }

  function elegirArchivoCsv(e: React.ChangeEvent<HTMLInputElement>) {
    const archivo = e.target.files?.[0];
    e.target.value = "";
    if (!archivo) return;
    setNombreArchivoCsv(archivo.name);
    setVistaPrevia(null);
    setResultadoCsv(null);
    setErrorCsv(null);
    const lector = new FileReader();
    lector.onload = async () => {
      const buffer = lector.result as ArrayBuffer;
      const texto = decodificarCsv(buffer);
      setArchivoCsvTexto(texto);
      setCargandoVistaPrevia(true);
      try {
        const res = await api.post("/inventario/importar-csv/vista-previa", { csv: texto });
        setVistaPrevia(res.data);
      } catch (err) {
        setErrorCsv(mensajeError(err));
      } finally {
        setCargandoVistaPrevia(false);
      }
    };
    lector.onerror = () => setErrorCsv("No se pudo leer el archivo. Intenta de nuevo.");
    lector.readAsArrayBuffer(archivo);
  }

  async function confirmarCargaCsv() {
    if (!archivoCsvTexto) return;
    if (!window.confirm("Esto va a crear y actualizar productos según el archivo. Todos los campos de los productos existentes se reemplazarán y la acción NO se puede deshacer. ¿Confirmas la carga?")) {
      return;
    }
    setConfirmando(true);
    setErrorCsv(null);
    try {
      const res = await api.post("/inventario/importar-csv/confirmar", { csv: archivoCsvTexto });
      setResultadoCsv(res.data);
      cargar();
    } catch (err) {
      setErrorCsv(mensajeError(err));
    } finally {
      setConfirmando(false);
    }
  }

  return (
    <div>
      <PageHeader
        titulo="Productos y Servicios"
        descripcion="Catálogo de productos y servicios: precios, stock y proveedores."
        icono={<Package size={20} />}
        acciones={
          <div className="flex flex-wrap items-center gap-2">
            <button className="btn-secondary" disabled={exportando} onClick={exportarInventario}>
              {exportando ? <Spinner className="h-4 w-4" /> : <Download size={16} />} Exportar CSV
            </button>
            {!soloLectura && (
              <button className="btn-secondary" onClick={abrirModalCsv}>
                <Upload size={16} /> Cargar CSV
              </button>
            )}
            {!soloLectura && (
              <button className="btn-primary" onClick={abrirCrear}>
                <Plus size={16} /> Nuevo producto/servicio
              </button>
            )}
          </div>
        }
      />

      <div className="card">
        <div className="flex items-center gap-2 border-b border-slate-100 p-4">
          <div className="relative flex-1 max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="input pl-9"
              placeholder="Buscar..."
              value={buscar}
              onChange={(e) => {
                setPagina(1);
                setBuscar(e.target.value);
              }}
            />
          </div>
        </div>

        {cargando ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : error ? (
          <div className="p-6 text-sm text-red-600">{error}</div>
        ) : items.length === 0 ? (
          <EmptyState
            titulo="No hay productos ni servicios todavía"
            descripcion='Crea el primero con "Nuevo producto/servicio" (ej. Baño normal, Vacuna tipo 1).'
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-3 font-medium">Nombre</th>
                    <th className="px-4 py-3 font-medium">Categoría</th>
                    <th className="px-4 py-3 font-medium">Precio venta</th>
                    <th className="px-4 py-3 font-medium">Stock</th>
                    <th className="px-4 py-3 font-medium">Caducidad</th>
                    <th className="px-4 py-3 font-medium">Proveedor</th>
                    <th className="px-4 py-3 text-right font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => {
                    const maneja = infiereManejaInventario(p);
                    return (
                      <tr key={p.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-700">{p.nombre}</td>
                        <td className="px-4 py-3">
                          <Badge color={p.categoria === "SERVICIO" ? "azul" : "gris"}>
                            {categorias.find((c) => c.value === p.categoria)?.label ?? p.categoria}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-700">${p.precioVenta.toLocaleString("es-MX")}</td>
                        <td className="px-4 py-3 text-slate-500">
                          {maneja ? `${p.stockActual} ${p.unidad}` : <span className="text-slate-300">No aplica</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {maneja && p.caducidad ? new Date(p.caducidad).toLocaleDateString("es-MX") : <span className="text-slate-300">No aplica</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {maneja && p.proveedor ? p.proveedor : <span className="text-slate-300">No aplica</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            <button className="btn-ghost !px-2" title="Editar" onClick={() => abrirEditar(p)}>
                              <Pencil size={15} />
                            </button>
                            {!soloLectura && (
                              <button className="btn-ghost !px-2 text-red-500" title="Eliminar" onClick={() => eliminar(p)}>
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPaginas > 1 && (
              <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
                <span className="text-xs text-slate-400">Página {pagina} de {totalPaginas}</span>
                <div className="flex gap-2">
                  <button className="btn-secondary !px-2" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
                    <ChevronLeft size={16} />
                  </button>
                  <button className="btn-secondary !px-2" disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)}>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Modal open={modalAbierto} onClose={() => setModalAbierto(false)} title={editando ? "Editar producto/servicio" : "Nuevo producto/servicio"}>
        <form onSubmit={guardar} className="space-y-4">
          <div>
            <label className="label">Nombre *</label>
            <input
              className="input"
              required
              placeholder="Ej. Baño premium, Vacuna tipo 1, Croquetas 3kg"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Categoría</label>
              <select className="input" value={form.categoria} onChange={(e) => cambiarCategoria(e.target.value)}>
                {categorias.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Unidad</label>
              <input className="input" value={form.unidad} onChange={(e) => setForm({ ...form, unidad: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input" rows={2} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">SKU</label>
              <input className="input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
            </div>
            <div>
              <label className="label">Precio de venta *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="input"
                required
                value={form.precioVenta}
                onChange={(e) => setForm({ ...form, precioVenta: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="label">Precio de compra</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="input"
              value={form.precioCompra}
              onChange={(e) => setForm({ ...form, precioCompra: e.target.value })}
            />
          </div>

          <label className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-400"
              checked={manejaFacturacion}
              onChange={(e) => setManejaFacturacion(e.target.checked)}
            />
            Definir datos para facturar este producto/servicio (CFDI)
          </label>

          {manejaFacturacion ? (
            <div className="rounded-xl border border-slate-100 p-3">
              <p className="label mb-2">Datos para facturar (CFDI)</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label text-xs">Clave de producto/servicio (SAT)</label>
                  <input
                    className="input"
                    list="claves-prodserv"
                    value={form.claveProdServ}
                    onChange={(e) => setForm({ ...form, claveProdServ: e.target.value })}
                  />
                  <datalist id="claves-prodserv">
                    {(catalogosFactura?.clavesProdServSugeridas ?? []).map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="label text-xs">Clave de unidad (SAT)</label>
                  <input
                    className="input"
                    list="claves-unidad"
                    value={form.claveUnidadSat}
                    onChange={(e) => setForm({ ...form, claveUnidadSat: e.target.value })}
                  />
                  <datalist id="claves-unidad">
                    {(catalogosFactura?.clavesUnidadSugeridas ?? []).map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="label text-xs">Objeto de impuesto</label>
                  <select
                    className="input"
                    value={form.objetoImpuesto}
                    onChange={(e) => setForm({ ...form, objetoImpuesto: e.target.value })}
                  >
                    {(catalogosFactura?.objetosImpuesto ?? []).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label text-xs">Tasa de IVA</label>
                  <select className="input" value={form.tasaIva} onChange={(e) => setForm({ ...form, tasaIva: e.target.value })}>
                    <option value="0.16">16% (tasa general)</option>
                    <option value="0">0% (exento / tasa 0%)</option>
                  </select>
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-400">
                Se usan cuando factures ventas que incluyan este producto/servicio. Si no las conoces, pregúntale a tu
                contador — los valores de aquí ya sirven para la mayoría de los casos.
              </p>
            </div>
          ) : (
            <p className="text-xs text-slate-400">
              Se usarán valores genéricos válidos para facturar. Marca la casilla de arriba si quieres afinarlos para este producto/servicio.
            </p>
          )}

          <label className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-400"
              checked={manejaInventario}
              onChange={(e) => setManejaInventario(e.target.checked)}
            />
            Este producto maneja inventario (stock, caducidad, proveedor)
          </label>

          {manejaInventario ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Stock actual</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="input"
                    value={form.stockActual}
                    onChange={(e) => setForm({ ...form, stockActual: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label">Stock mínimo</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="input"
                    value={form.stockMinimo}
                    onChange={(e) => setForm({ ...form, stockMinimo: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Fecha de caducidad</label>
                  <input
                    type="date"
                    className="input"
                    value={form.caducidad}
                    onChange={(e) => setForm({ ...form, caducidad: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label">Proveedor</label>
                  <input className="input" value={form.proveedor} onChange={(e) => setForm({ ...form, proveedor: e.target.value })} />
                </div>
              </div>
            </>
          ) : (
            <p className="text-xs text-slate-400">
              Stock, caducidad y proveedor no aplican para este producto. Marca la casilla de arriba si en algún momento quieres definirlos.
            </p>
          )}

          {errorForm && <p className="text-sm text-red-600">{errorForm}</p>}

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button type="button" className="btn-secondary" onClick={() => setModalAbierto(false)}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={guardando}>
              {guardando && <Spinner className="h-4 w-4 text-white" />}
              Guardar
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={modalCsvAbierto} onClose={() => setModalCsvAbierto(false)} title="Cargar productos y servicios desde CSV">
        <div className="space-y-4">
          {!resultadoCsv && (
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
              <p className="text-sm text-slate-600">
                ¿Primera vez? <button type="button" className="font-medium text-brand-600 hover:underline" onClick={descargarPlantilla}>Descarga la plantilla</button> para llenarla con el formato correcto.
              </p>
            </div>
          )}

          {!resultadoCsv && (
            <div>
              <input ref={inputCsvRef} type="file" accept=".csv,text/csv" className="hidden" onChange={elegirArchivoCsv} />
              <button type="button" className="btn-secondary w-full justify-center" onClick={() => inputCsvRef.current?.click()}>
                <FileSpreadsheet size={16} /> {nombreArchivoCsv || "Selecciona tu archivo .csv"}
              </button>
            </div>
          )}

          {cargandoVistaPrevia && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500">
              <Spinner className="h-5 w-5" /> Revisando el archivo...
            </div>
          )}

          {errorCsv && (
            <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {errorCsv}
            </div>
          )}

          {resultadoCsv && (
            <div className="flex items-start gap-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-4 text-sm">
              <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Productos y servicios actualizados correctamente.</p>
                <p>Se crearon {resultadoCsv.creados} producto(s) y se actualizaron {resultadoCsv.actualizados}.</p>
              </div>
            </div>
          )}

          {vistaPrevia?.errorGeneral && (
            <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {vistaPrevia.errorGeneral}
            </div>
          )}

          {vistaPrevia && !vistaPrevia.errorGeneral && !resultadoCsv && (
            <>
              <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 text-sm">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Antes de confirmar, revisa esto:</p>
                  <p>
                    Se crearán <strong>{vistaPrevia.resumen.crear}</strong> producto(s) nuevo(s) y se actualizarán{" "}
                    <strong>{vistaPrevia.resumen.actualizar}</strong> ya existente(s) (se emparejan por SKU). En los que se
                    actualicen, <strong>todos los campos se reemplazarán</strong> por lo que diga el archivo. Esta acción{" "}
                    <strong>no se puede deshacer</strong>. Los productos que no aparezcan en el archivo no se tocan ni se
                    eliminan.
                  </p>
                </div>
              </div>

              {vistaPrevia.resumen.errores > 0 && (
                <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <p>
                    Hay <strong>{vistaPrevia.resumen.errores}</strong> fila(s) con errores. Corrígelas en tu archivo y vuelve a
                    subirlo — mientras haya errores, no se puede confirmar la carga.
                  </p>
                </div>
              )}

              <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-100">
                <table className="w-full text-left text-xs">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="border-b border-slate-100 uppercase tracking-wide text-slate-400">
                      <th className="px-3 py-2 font-medium">Fila</th>
                      <th className="px-3 py-2 font-medium">Acción</th>
                      <th className="px-3 py-2 font-medium">SKU</th>
                      <th className="px-3 py-2 font-medium">Nombre</th>
                      <th className="px-3 py-2 font-medium">Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vistaPrevia.filas.map((f) => (
                      <tr key={f.fila} className="border-b border-slate-50 last:border-0 align-top">
                        <td className="px-3 py-2 text-slate-400">{f.fila}</td>
                        <td className="px-3 py-2">
                          {f.accion === "crear" && <Badge color="verde">Crear</Badge>}
                          {f.accion === "actualizar" && <Badge color="azul">Actualizar</Badge>}
                          {f.accion === "error" && <Badge color="rojo">Error</Badge>}
                        </td>
                        <td className="px-3 py-2 text-slate-500">{f.sku || "—"}</td>
                        <td className="px-3 py-2 text-slate-700">{f.nombre || "—"}</td>
                        <td className="px-3 py-2">
                          {f.accion === "error" ? (
                            <ul className="space-y-1 text-red-600">
                              {f.errores.map((e, i) => (
                                <li key={i}>
                                  <span className="font-medium">{e.columna}:</span> {e.mensaje}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-slate-500">
                              ${f.datos?.precioVenta?.toLocaleString("es-MX")} · stock {f.datos?.stockActual}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button type="button" className="btn-secondary" onClick={() => setModalCsvAbierto(false)}>
              {resultadoCsv ? "Cerrar" : "Cancelar"}
            </button>
            {vistaPrevia && !vistaPrevia.errorGeneral && !resultadoCsv && (
              <button
                type="button"
                className="btn-primary"
                disabled={confirmando || vistaPrevia.resumen.errores > 0}
                onClick={confirmarCargaCsv}
              >
                {confirmando && <Spinner className="h-4 w-4 text-white" />}
                Confirmar carga
              </button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
